import { z } from 'zod';

/**
 * Modul „Verbesserungen" (ADR 0078): Bug-/Verbesserungs-Meldungen der Nutzer.
 *
 * Der fachliche Kern dieses Schemas ist der **Nudge zur Detailtiefe**: Meldungen
 * sollen so ankommen, dass der Systemverwalter sie ohne Rückfrage-Runde bewerten
 * kann. Drei Ebenen, alle hier verankert (also identisch in Formular und API):
 *
 * 1. `FEEDBACK_FIELDS` — Struktur statt Freitext-Blackbox: getrennte Felder für
 *    Ist, Soll und Zusatzkontext, mit typ-spezifischen Labels + Hilfetexten.
 * 2. `FEEDBACK_MIN_LENGTHS` — Mindestlängen, vom Server mit 422 erzwungen
 *    (nicht per DevTools umgehbar).
 * 3. `scoreFeedbackDetail()` — Live-Bewertung mit konkreten Hinweisen, genutzt
 *    im Formular (Fortschritt) und in der Verwalter-Liste (Spalte „Detailgrad").
 *    Bewusst KEIN Absende-Gate über die Mindestlängen hinaus.
 */

export const feedbackTypeSchema = z.enum(['BUG', 'IMPROVEMENT']);
export type FeedbackType = z.infer<typeof feedbackTypeSchema>;

export const feedbackStatusSchema = z.enum([
  'NEW',
  'NEEDS_INFO',
  'ACCEPTED',
  'IN_PROGRESS',
  'DONE',
  'DECLINED',
]);
export type FeedbackStatus = z.infer<typeof feedbackStatusSchema>;

export const feedbackSeveritySchema = z.enum(['LOW', 'MEDIUM', 'HIGH', 'BLOCKER']);
export type FeedbackSeverity = z.infer<typeof feedbackSeveritySchema>;

export const FEEDBACK_TYPE_LABELS: Record<FeedbackType, string> = {
  BUG: 'Fehler',
  IMPROVEMENT: 'Verbesserung',
};

export const FEEDBACK_STATUS_LABELS: Record<FeedbackStatus, string> = {
  NEW: 'Neu',
  NEEDS_INFO: 'Rückfrage offen',
  ACCEPTED: 'Angenommen',
  IN_PROGRESS: 'In Umsetzung',
  DONE: 'Umgesetzt',
  DECLINED: 'Abgelehnt',
};

/**
 * Schweregrad — bei `IMPROVEMENT` liest sich derselbe Wert als Dringlichkeit.
 * Zwei Label-Sätze, damit im UI nicht „Blockierend" an einem Wunsch steht.
 */
export const FEEDBACK_SEVERITY_LABELS: Record<FeedbackType, Record<FeedbackSeverity, string>> = {
  BUG: {
    LOW: 'Kleinigkeit',
    MEDIUM: 'Störend',
    HIGH: 'Schwerwiegend',
    BLOCKER: 'Blockiert die Arbeit',
  },
  IMPROVEMENT: {
    LOW: 'Nice to have',
    MEDIUM: 'Wäre hilfreich',
    HIGH: 'Wichtig',
    BLOCKER: 'Dringend nötig',
  },
};

/**
 * Betroffene Bereiche für die Triage — abgeleitet aus den Navigations-Bereichen
 * (`NAV_GROUPS`), aber absichtlich eine eigene, flache Liste: der Melder denkt
 * in „wo war ich", nicht in Sidebar-Gruppen. In der DB steht der Wert als
 * Freitext (`area`), damit spätere Umbenennungen keine Migration brauchen.
 */
export const FEEDBACK_AREAS = [
  'Dashboard / Startseite',
  'Analyse & Auswertungen',
  'Selektionspool',
  'Projekte',
  'Projekt-Zeitplan',
  'Verkauf & Angebote',
  'Vertriebspipeline',
  'CRM / Kunden & Lieferanten',
  'Referenzen',
  'Angebotskalkulation',
  'Engineering / Zeichnungsnummern',
  'Stücklisten (Technik)',
  'Materialcodes & Warengruppen',
  'QM',
  'Artikel & Lager',
  'Materialbedarf',
  'Inventur',
  'Beschaffung',
  'Bedarfsvorschau',
  'Wareneingang',
  'Kommissionierung',
  'Transporte',
  'Sperrlager',
  'Montage & Wartung',
  'Buchhaltung',
  'Reporting',
  'Zeiterfassung',
  'Urlaub & Fehlzeiten',
  'Reisekosten',
  'Personen & Rollen',
  'Einstellungen',
  'Datenbank & Import',
  'Audit-Log',
  'Suche',
  'Teamchat',
  'Benachrichtigungen',
  'Anmeldung & Konto',
  'Dateien / Dokumente',
  // Das Modul selbst — fehlte zuerst, obwohl es die erste Meldung darüber gab.
  // Der Guard-Test in apps/web (feedbackAreas.spec.ts) hält die Liste seither
  // gegen die Navigation gegen, damit kein Bereich mehr durchfällt.
  'Verbesserungen (dieses Modul)',
  'Etwas anderes / weiß nicht',
] as const;
export type FeedbackArea = (typeof FEEDBACK_AREAS)[number];

/** Die drei Detail-Spalten am FeedbackItem (typ-abhängig anders gelabelt). */
export const FEEDBACK_FIELD_KEYS = ['situation', 'expectation', 'extraDetails'] as const;
export type FeedbackFieldKey = (typeof FEEDBACK_FIELD_KEYS)[number];

export interface FeedbackField {
  key: FeedbackFieldKey;
  label: string;
  /** Hilfetext — im UI unter dem Label, nicht als Vorbelegung. */
  hint: string;
  /** Platzhalter im Textfeld: zeigt an einem Beispiel, was gemeint ist. */
  placeholder: string;
}

/**
 * Welche Bedeutung die drei Detail-Spalten je Meldungsart haben (ADR 0078 §1) —
 * zentrale Definition für Formular, Detailansicht und Validierungs-Meldungen.
 * Muster: `QM_STEPS` (ADR 0054), wo Spalten ebenfalls typ-abhängig unter
 * eigenen Labels wiederverwendet werden.
 */
export const FEEDBACK_FIELDS: Record<FeedbackType, FeedbackField[]> = {
  BUG: [
    {
      key: 'situation',
      label: 'Was passiert?',
      hint: 'Das Fehlerbild: Was hast du gemacht, was ist dann passiert? Wenn eine Fehlermeldung kam, schreib sie wörtlich hin.',
      placeholder:
        'z. B. Beim Speichern des Lieferscheins bleibt der Knopf grau und es kommt die Meldung „Validierung fehlgeschlagen". Der Beleg ist danach nicht gespeichert.',
    },
    {
      key: 'expectation',
      label: 'Was hättest du erwartet?',
      hint: 'Was hätte stattdessen passieren sollen? Klingt banal, trennt aber „kaputt" von „funktioniert anders als gedacht".',
      placeholder: 'z. B. Der Lieferschein wird gespeichert und ich lande wieder in der Belegliste.',
    },
    {
      key: 'extraDetails',
      label: 'Schritte zum Nachstellen',
      hint: 'Der wichtigste Teil: Was muss ich klicken, um denselben Fehler zu sehen? Nummeriert, vom Start weg. Tritt es immer auf oder nur manchmal?',
      placeholder:
        'z. B.\n1. Verkauf → Beleg 2026-0142 öffnen\n2. Position 3 auf Menge 0 setzen\n3. Auf „Speichern" klicken\n→ passiert jedes Mal',
    },
  ],
  IMPROVEMENT: [
    {
      key: 'situation',
      label: 'Wie läuft es heute?',
      hint: 'Der heutige Weg: Was machst du aktuell, welche Umwege, wie viele Klicks oder welche Liste daneben?',
      placeholder:
        'z. B. Um zu sehen, welche Bestellungen zu einem Projekt offen sind, öffne ich jede Bestellung einzeln und notiere mir die Positionen in Excel.',
    },
    {
      key: 'expectation',
      label: 'Was wünschst du dir?',
      hint: 'Wie sollte es idealerweise funktionieren? Ruhig konkret bis zur Bildschirm-Ebene — welcher Knopf, welche Spalte, welche Liste.',
      placeholder:
        'z. B. Im Projekt ein Reiter „Offene Bestellungen" mit Artikel, Menge, Lieferant und erwartetem Liefertermin.',
    },
    {
      key: 'extraDetails',
      label: 'Was bringt das?',
      hint: 'Hilft mir beim Priorisieren: Wie oft kommt das vor, wie viel Zeit oder Ärger spart es, und wer außer dir hat das Problem?',
      placeholder:
        'z. B. Mache ich 2–3× pro Woche, dauert jedes Mal ~20 Minuten. Betrifft auch die Projektleitung bei Kundenrückfragen.',
    },
  ],
};

/** Feld-Definition einer Art nachschlagen (Labels für Fehlermeldungen/Detailseite). */
export function feedbackField(type: FeedbackType, key: FeedbackFieldKey): FeedbackField {
  const field = FEEDBACK_FIELDS[type].find((entry) => entry.key === key);
  // FEEDBACK_FIELDS enthält je Art alle FEEDBACK_FIELD_KEYS — der Fallback ist
  // nur da, damit der Rückgabetyp nicht optional wird.
  return field ?? FEEDBACK_FIELDS[type][0];
}

/**
 * Mindestlängen je Feld (ADR 0078 §2, Ebene 2). Absichtlich niedrig: sie sollen
 * Ein-Wort-Meldungen („geht nicht") abweisen, nicht knappe, präzise Meldungen
 * bestrafen. Eine Anpassung passiert genau hier — Formular und API teilen sie.
 */
export const FEEDBACK_MIN_LENGTHS = {
  // 15 statt der zuerst gewählten 10: bei 10 kam „geht nicht" (genau 10 Zeichen)
  // als Titel durch — der Test in feedback.schema.spec.ts hat das aufgedeckt.
  // 15 verlangt einen kurzen Satzteil („Login schlägt fehl") und lässt knappe,
  // aber aussagekräftige Titel weiter zu.
  title: 15,
  situation: 40,
  expectation: 20,
  extraDetails: 20,
} as const;

/**
 * Ab dieser Länge gilt ein Feld als ausführlich beschrieben (Detailgrad-Bonus).
 *
 * Kalibriert an einer *vorbildlichen* Meldung (zwei Sätze Fehlerbild mit
 * wörtlicher Meldung, ein Satz Erwartung, drei nummerierte Schritte): die muss
 * die volle Punktzahl erreichen und darf NICHT als „knapp" gemahnt werden. Beim
 * ersten Kalibrieren lagen die Werte höher (160/60/120) — dort bekam genau so
 * eine Meldung noch Hinweise angezeigt, was den Melder zu Geschwätzigkeit
 * erzieht statt zu Präzision. Wer hier schraubt: `feedback.schema.spec.ts`
 * hält die Referenz-Meldung fest.
 */
const FEEDBACK_RICH_LENGTHS = {
  situation: 120,
  expectation: 50,
  extraDetails: 90,
} as const;

const detailText = (key: keyof typeof FEEDBACK_MIN_LENGTHS, label: string) =>
  z
    .string()
    .trim()
    .min(FEEDBACK_MIN_LENGTHS[key], {
      message: `${label}: bitte etwas ausführlicher (mindestens ${FEEDBACK_MIN_LENGTHS[key]} Zeichen) — genau daran hängt, ob die Meldung ohne Rückfrage bearbeitet werden kann.`,
    })
    .max(10000);

export const createFeedbackItemSchema = z.object({
  type: feedbackTypeSchema,
  severity: feedbackSeveritySchema.default('MEDIUM'),
  title: detailText('title', 'Titel'),
  situation: detailText('situation', 'Ausgangslage'),
  expectation: detailText('expectation', 'Erwartung'),
  extraDetails: detailText('extraDetails', 'Details'),
  /** Freitext, im UI Auswahl aus FEEDBACK_AREAS. */
  area: z.string().trim().max(120).optional(),
  /** Vom Frontend automatisch belegt (Seite, auf der die Meldung entstand). */
  pagePath: z.string().trim().max(500).optional(),
  appVersion: z.string().trim().max(50).optional(),
});
export type CreateFeedbackItemInput = z.infer<typeof createFeedbackItemSchema>;

/**
 * Nachbessern der EIGENEN Meldung (`feedback.update`) — bewusst nur die
 * inhaltlichen Felder. Status/Schweregrad/Bereich setzt der Verwalter über
 * `triageFeedbackItemSchema`; sonst könnte sich jeder seine Meldung selbst
 * auf „dringend" oder „umgesetzt" stellen.
 */
export const updateFeedbackItemSchema = z
  .object({
    title: detailText('title', 'Titel'),
    situation: detailText('situation', 'Ausgangslage'),
    expectation: detailText('expectation', 'Erwartung'),
    extraDetails: detailText('extraDetails', 'Details'),
    area: z.string().trim().max(120).nullable(),
  })
  .partial();
export type UpdateFeedbackItemInput = z.infer<typeof updateFeedbackItemSchema>;

/** Verwalter-Vorgang (`feedback.manage`): Status, Einordnung, Abschluss-Notiz. */
export const triageFeedbackItemSchema = z
  .object({
    status: feedbackStatusSchema,
    severity: feedbackSeveritySchema,
    type: feedbackTypeSchema,
    area: z.string().trim().max(120).nullable(),
    resolutionNote: z.string().trim().max(5000).nullable(),
  })
  .partial();
export type TriageFeedbackItemInput = z.infer<typeof triageFeedbackItemSchema>;

export const createFeedbackCommentSchema = z.object({
  body: z.string().trim().min(2, { message: 'Der Kommentar ist leer.' }).max(5000),
  /**
   * Interne Verwalter-Notiz — für den Melder nicht sichtbar. Wird nur mit
   * `feedback.manage` akzeptiert (Service prüft das, nicht das Schema).
   */
  isInternal: z.boolean().default(false),
});
export type CreateFeedbackCommentInput = z.infer<typeof createFeedbackCommentSchema>;

export const feedbackCommentResponseSchema = z.object({
  id: z.string().uuid(),
  itemId: z.string().uuid(),
  authorUserId: z.string().uuid(),
  authorName: z.string().nullable(),
  body: z.string(),
  isInternal: z.boolean(),
  createdAt: z.string().datetime(),
});
export type FeedbackCommentResponse = z.infer<typeof feedbackCommentResponseSchema>;

/**
 * Erlaubte Anhang-Typen einer Meldung (ADR 0078 Phase B, erweitert 2026-08-03 auf
 * Dokumente — Tech-Lead: „Excel, PDF, docx sollen auch gehen").
 *
 * Bewusst eine Allowlist statt `image/*` bzw. „alles": SVG ist ein XML-Dokument
 * mit Skriptfähigkeit und gehört NICHT dazu, auch wenn Browser es als Bild
 * anzeigen. Ausführbares (exe/zip/js) ebenso nicht. Die Auslieferung ist
 * zusätzlich gehärtet (`sendSecureFile`: nosniff + CSP-Sandbox, skriptbare Typen
 * immer als Download) — die Allowlist ist die erste, nicht die einzige Grenze.
 */
export const FEEDBACK_ATTACHMENT_MIME_TYPES = [
  // Bildschirmfotos — der häufigste Fall.
  'image/png',
  'image/jpeg',
  'image/webp',
  'image/gif',
  'application/pdf',
  // Excel: xlsx, xls und die Tabellen-Nachbarn CSV/Text.
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.ms-excel',
  'text/csv',
  'text/plain',
  // Word: docx, doc.
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/msword',
  // PowerPoint: pptx, ppt (Mockups/Skizzen kommen oft als Folie).
  'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  'application/vnd.ms-powerpoint',
] as const;

/**
 * Dateiendungen der erlaubten Typen. Browser und Betriebssysteme melden für
 * Office-Dateien nicht verlässlich einen MIME-Typ (leerer `file.type` bei
 * Drag&Drop aus manchen Quellen, `application/octet-stream` bei anderen) — die
 * Prüfung akzeptiert deshalb ZUSÄTZLICH anhand der Endung, und das `accept`-
 * Attribut nennt beides.
 */
export const FEEDBACK_ATTACHMENT_EXTENSIONS = [
  '.png', '.jpg', '.jpeg', '.webp', '.gif',
  '.pdf',
  '.xlsx', '.xls', '.csv', '.txt',
  '.docx', '.doc',
  '.pptx', '.ppt',
] as const;

/** Kurzliste für Fehlermeldungen und Hinweise in der Oberfläche. */
export const FEEDBACK_ATTACHMENT_HINT = 'Bilder, PDF, Excel, Word, PowerPoint, CSV/Text';

/**
 * Ist diese Datei als Anhang erlaubt? MIME-Typ ODER Endung muss passen — siehe
 * `FEEDBACK_ATTACHMENT_EXTENSIONS` zur Begründung. Server und Browser nutzen
 * dieselbe Funktion, damit die Grenzen nicht auseinanderlaufen.
 */
export function isAllowedFeedbackAttachment(fileName: string, mimeType: string): boolean {
  if ((FEEDBACK_ATTACHMENT_MIME_TYPES as readonly string[]).includes(mimeType)) return true;
  const lower = fileName.toLowerCase();
  return (FEEDBACK_ATTACHMENT_EXTENSIONS as readonly string[]).some((ext) => lower.endsWith(ext));
}

/**
 * Größenobergrenze je Anhang (Server prüft mit). 25 MB statt der früheren 10:
 * Bildschirmfotos sind klein, eine Excel-Mappe mit Bildern oder ein
 * gescanntes PDF sprengt 10 MB aber schnell.
 */
export const FEEDBACK_ATTACHMENT_MAX_BYTES = 25 * 1024 * 1024;

export const feedbackAttachmentResponseSchema = z.object({
  id: z.string().uuid(),
  itemId: z.string().uuid(),
  fileName: z.string(),
  mimeType: z.string(),
  sizeBytes: z.number().int().nonnegative(),
  note: z.string().nullable(),
  createdAt: z.string().datetime(),
});
export type FeedbackAttachmentResponse = z.infer<typeof feedbackAttachmentResponseSchema>;

/** Metadaten beim Hochladen (Query-Parameter; die Bytes stehen im Body). */
export const uploadFeedbackAttachmentSchema = z.object({
  fileName: z.string().trim().min(1).max(200),
  note: z.string().trim().max(300).nullish(),
});
export type UploadFeedbackAttachmentInput = z.infer<typeof uploadFeedbackAttachmentSchema>;

/**
 * Auswahl für den Paket-Export (ADR 0078 §7). Als Body und nicht als
 * Query-Liste: Bei zwei Dutzend Meldungen wäre die URL sonst am Längenlimit,
 * und `?ids=` ist bei Mehrfachwerten je Server unterschiedlich ausgelegt.
 */
export const exportFeedbackZipSchema = z.object({
  itemIds: z.array(z.string().uuid()).min(1, { message: 'Keine Meldung ausgewählt.' }).max(200),
});
export type ExportFeedbackZipInput = z.infer<typeof exportFeedbackZipSchema>;

export const feedbackItemResponseSchema = z.object({
  id: z.string().uuid(),
  number: z.string(),
  type: feedbackTypeSchema,
  status: feedbackStatusSchema,
  severity: feedbackSeveritySchema,
  title: z.string(),
  situation: z.string(),
  expectation: z.string(),
  extraDetails: z.string(),
  area: z.string().nullable(),
  pagePath: z.string().nullable(),
  appVersion: z.string().nullable(),
  userAgent: z.string().nullable(),
  reporterUserId: z.string().uuid(),
  reporterName: z.string().nullable(),
  resolutionNote: z.string().nullable(),
  /** Detailgrad 0–100 (`scoreFeedbackDetail`) — serverseitig mitgeliefert. */
  detailScore: z.number().int().min(0).max(100),
  commentCount: z.number().int().min(0),
  /** Angehängte Screenshots (Metadaten; die Bytes über den Content-Endpoint). */
  attachments: z.array(feedbackAttachmentResponseSchema),
  /** true, wenn der abrufende Nutzer der Melder ist (UI: „meine Meldung"). */
  isOwnItem: z.boolean(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});
export type FeedbackItemResponse = z.infer<typeof feedbackItemResponseSchema>;

// ── Detailgrad-Bewertung ────────────────────────────────────────────────────

export interface FeedbackDetailScore {
  /** 0–100. */
  score: number;
  /**
   * Ausführliche, umsetzbare Hinweise (enthalten den Hilfetext des Feldes) —
   * leer, wenn nichts mehr fehlt. Für Ansichten, die das Formular NICHT daneben
   * haben: die Verwalter-Liste und der Vorgang.
   */
  suggestions: string[];
  /**
   * Nur die Bezeichnungen der offenen Punkte („Titel", „Was passiert?", …) —
   * für die knappe Anzeige IM Formular, wo der Hilfetext schon am Feld steht und
   * eine ausführliche Liste die Eingabefelder verdecken würde.
   */
  openLabels: string[];
}

/** Was `scoreFeedbackDetail` an Eingabe braucht (Formular-Zwischenstand oder Datensatz). */
export interface FeedbackScoreInput {
  type: FeedbackType;
  title?: string | null;
  situation?: string | null;
  expectation?: string | null;
  extraDetails?: string | null;
  area?: string | null;
  pagePath?: string | null;
}

const length = (value: string | null | undefined): number => (value ?? '').trim().length;

/**
 * Bewertet, wie gut eine Meldung bearbeitbar ist (ADR 0078 §2, Ebene 3).
 * Reine Funktion, damit Formular (live) und Backend (Listen-Spalte) dieselbe
 * Zahl zeigen.
 *
 * Gewichtung: die drei Detailfelder tragen 75 Punkte (je 25 — Mindestlänge
 * erreicht = 15, ausführlich = 25), Titel 10, Bereich 10, Seitenpfad 5.
 * Bewusst eine Heuristik: eine kurze, präzise Meldung kann unterschätzt werden,
 * deshalb blockiert die Zahl nirgends das Absenden.
 */
export function scoreFeedbackDetail(input: FeedbackScoreInput): FeedbackDetailScore {
  const suggestions: string[] = [];
  const openLabels: string[] = [];
  let score = 0;

  if (length(input.title) >= FEEDBACK_MIN_LENGTHS.title) {
    score += 10;
  } else {
    suggestions.push('Der Titel ist noch sehr knapp — ein Satz, der das Problem benennt, hilft beim Wiederfinden.');
    openLabels.push('Titel');
  }

  for (const key of FEEDBACK_FIELD_KEYS) {
    const field = feedbackField(input.type, key);
    const value = length(input[key]);
    if (value >= FEEDBACK_RICH_LENGTHS[key]) {
      score += 25;
    } else if (value >= FEEDBACK_MIN_LENGTHS[key]) {
      score += 15;
      suggestions.push(`„${field.label}" ist ausreichend, aber knapp — mehr Konkretes hilft: ${field.hint}`);
      openLabels.push(`${field.label} (knapp)`);
    } else {
      suggestions.push(`„${field.label}" fehlt noch oder ist zu kurz. ${field.hint}`);
      openLabels.push(field.label);
    }
  }

  if (length(input.area) > 0) {
    score += 10;
  } else {
    suggestions.push('Bitte den betroffenen Bereich wählen — danach wird sortiert und zugeordnet.');
    openLabels.push('Betroffener Bereich');
  }

  if (length(input.pagePath) > 0) {
    score += 5;
  }

  return { score: Math.min(score, 100), suggestions, openLabels };
}

/** Ampel-Einordnung des Detailgrads für die UI (Badge-Ton, Verwalter-Liste). */
export function feedbackDetailLevel(score: number): 'low' | 'medium' | 'high' {
  if (score >= 80) return 'high';
  if (score >= 50) return 'medium';
  return 'low';
}
